import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

import { updateReviewsResponseVoteState } from './review-state'
import { ProductReview, ProductReviewsResponse, ProductReviewVote } from './types'

type VoteRequest = (params: { reviewId: string; vote: ProductReviewVote }) => {
	unwrap: () => Promise<ProductReview | undefined>
}

type UseDebouncedReviewVoteParams = {
	setData: Dispatch<SetStateAction<ProductReviewsResponse>>
	voteForProductReview: VoteRequest
}

export const useDebouncedReviewVote = ({ setData, voteForProductReview }: UseDebouncedReviewVoteParams) => {
	const [votingReviewId, setVotingReviewId] = useState<string | null>(null)
	const voteRequestIdsRef = useRef<Record<string, number>>({})
	const voteTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
	const voteCancelResolversRef = useRef<Record<string, (review?: ProductReview) => void>>({})
	const voteRollbackDataRef = useRef<Record<string, ProductReviewsResponse>>({})

	useEffect(() => {
		return () => {
			Object.values(voteTimersRef.current).forEach(clearTimeout)
			Object.values(voteCancelResolversRef.current).forEach(resolve => resolve(undefined))
			voteTimersRef.current = {}
			voteCancelResolversRef.current = {}
			voteRollbackDataRef.current = {}
		}
	}, [])

	const request = useCallback(
		(reviewId: string, vote: ProductReviewVote) => {
			if (voteTimersRef.current[reviewId]) {
				clearTimeout(voteTimersRef.current[reviewId])
				delete voteTimersRef.current[reviewId]
				voteCancelResolversRef.current[reviewId]?.(undefined)
				delete voteCancelResolversRef.current[reviewId]
			}

			const requestId = (voteRequestIdsRef.current[reviewId] ?? 0) + 1
			voteRequestIdsRef.current[reviewId] = requestId

			const isLatestRequest = () => voteRequestIdsRef.current[reviewId] === requestId
			let optimisticReview: ProductReview | undefined

			setVotingReviewId(reviewId)
			setData(current => {
				if (!voteRollbackDataRef.current[reviewId]) {
					voteRollbackDataRef.current[reviewId] = current
				}

				const nextData = updateReviewsResponseVoteState(current, reviewId, vote)
				optimisticReview =
					nextData.reviews.find(review => review.id === reviewId) ??
					nextData.mediaItems?.find(item => item.review.id === reviewId || item.reviewId === reviewId)?.review ??
					nextData.mediaReviews?.find(review => review.id === reviewId)

				return nextData
			})

			return new Promise<ProductReview | undefined>(resolve => {
				voteCancelResolversRef.current[reviewId] = resolve
				voteTimersRef.current[reviewId] = setTimeout(async () => {
					delete voteTimersRef.current[reviewId]
					delete voteCancelResolversRef.current[reviewId]

					try {
						const updatedReview = await voteForProductReview({ reviewId, vote }).unwrap()
						if (!isLatestRequest()) {
							resolve(undefined)
							return
						}

						if (updatedReview) {
							setData(current => updateReviewsResponseVoteState(current, reviewId, vote, updatedReview))
						}

						resolve(updatedReview ?? optimisticReview)
					} catch {
						if (isLatestRequest() && voteRollbackDataRef.current[reviewId]) {
							setData(voteRollbackDataRef.current[reviewId])
						}
						resolve(undefined)
					} finally {
						if (isLatestRequest()) {
							delete voteRequestIdsRef.current[reviewId]
							delete voteRollbackDataRef.current[reviewId]
							setVotingReviewId(null)
						}
					}
				}, 2000)
			})
		},
		[setData, voteForProductReview]
	)

	return { reviewId: votingReviewId, request }
}
